/**
 * Editable ECG marker definitions - SINGLE SOURCE OF TRUTH.
 * Type values match backend EcgAnnotationTypes C# enum.
 * To add a new editable marker type, add it here and it will appear in the toolbar.
 */
export const EDITABLE_MARKERS = [
  // QRS Complex
  { type: 39, group: 'qrs', label: '(', displayLabel: '(', color: '#607D8B', descKey: 'markerEditing.desc.wave_start' },
  { type: 48, group: 'qrs', label: 'R', displayLabel: null, color: '#2E7D32', descKey: 'markerEditing.desc.R' },
  { type: 17, group: 'qrs', label: 'Q', displayLabel: 'Q', color: '#1565C0', descKey: 'markerEditing.desc.Q' },
  { type: 50, group: 'qrs', label: 'S', displayLabel: 'S', color: '#7B1FA2', descKey: 'markerEditing.desc.S' },
  { type: 40, group: 'qrs', label: 'J', displayLabel: 'J', color: '#455A64', descKey: 'markerEditing.desc.wave_end' },
  // P Wave
  { type: 42, group: 'pWave', label: '(P', displayLabel: '(P', color: '#FF9800', descKey: 'markerEditing.desc.p_start' },
  { type: 24, group: 'pWave', label: 'P', displayLabel: 'P', color: '#F57C00', descKey: 'markerEditing.desc.P' },
  { type: 43, group: 'pWave', label: 'P)', displayLabel: 'P)', color: '#E65100', descKey: 'markerEditing.desc.p_end' },
  // T Wave
  { type: 44, group: 'tWave', label: '(T', displayLabel: '(T', color: '#F44336', descKey: 'markerEditing.desc.t_start' },
  { type: 27, group: 'tWave', label: 'T', displayLabel: 'T', color: '#D32F2F', descKey: 'markerEditing.desc.T' },
  { type: 45, group: 'tWave', label: 'T)', displayLabel: 'T)', color: '#B71C1C', descKey: 'markerEditing.desc.t_end' },
  // Other
  { type: 29, group: 'other', label: 'U', displayLabel: 'U', color: '#C2185B', descKey: 'markerEditing.desc.U' },
];

/**
 * Marker groups for UI organization.
 * Built from EDITABLE_MARKERS - no need to maintain separately.
 */
export const MARKER_GROUPS = {
  qrs: {
    labelKey: 'markerEditing.groups.qrs',
    markers: EDITABLE_MARKERS.filter(m => m.group === 'qrs')
  },
  pWave: {
    labelKey: 'markerEditing.groups.pWave',
    markers: EDITABLE_MARKERS.filter(m => m.group === 'pWave')
  },
  tWave: {
    labelKey: 'markerEditing.groups.tWave',
    markers: EDITABLE_MARKERS.filter(m => m.group === 'tWave')
  },
  other: {
    labelKey: 'markerEditing.groups.other',
    markers: EDITABLE_MARKERS.filter(m => m.group === 'other')
  }
};

// Type constants matching backend EcgAnnotationTypes enum
export const R_PEAK_TYPE = 48;  // EcgAnnotationTypes.R
export const r_PEAK_TYPE = 47;  // EcgAnnotationTypes.r (lowercase variant)
export const NOT_QRS_TYPE = 0;  // EcgAnnotationTypes.notQRS (fallback)

/**
 * Progressive proximity thresholds for marker detection (in milliseconds).
 * When user clicks, we first try to find a marker within the smallest threshold.
 * If not found, we expand to the next threshold, and so on.
 * This allows precise clicking to select specific markers when they're close together.
 *
 * Configure these values based on user feedback:
 * - Smaller values = more precise clicking required
 * - Larger values = easier to click but less precision
 */
export const PROXIMITY_THRESHOLDS_MS = [2, 7, 15];

/**
 * Check if a marker type represents an R-peak (R or r)
 * @param {number} type - Marker type as int
 * @returns {boolean}
 */
export const isRPeakType = (type) => type === R_PEAK_TYPE || type === r_PEAK_TYPE;

/**
 * Get marker definition by type value
 * @param {number} type - EcgAnnotationTypes value
 * @returns {Object|undefined} Marker definition or undefined
 */
export const getMarkerByType = (type) => EDITABLE_MARKERS.find(m => m.type === type);

/**
 * Get display label for a marker type.
 * Returns null for R-peaks (they show interval ID instead).
 * @param {number} type - Marker type as int
 * @returns {string|null} Display label or null
 */
export const getMarkerDisplayLabel = (type) => {
  const marker = getMarkerByType(type);
  return marker?.displayLabel ?? null;
};

/**
 * Manages ECG marker editing operations (add/delete markers).
 * Tracks pending changes until saved to the server.
 * Uses numeric marker types matching EcgAnnotationTypes enum.
 */
class MarkerEditingManager {
  constructor() {
    this.originalMarkers = [];
    this.pendingAdditions = [];
    this.pendingDeletions = [];
    // Max proximity for general filtering (use largest threshold)
    this.PROXIMITY_MS = Math.max(...PROXIMITY_THRESHOLDS_MS);
    this.selectedMarkerType = R_PEAK_TYPE; // Default to R-peak
  }

  /**
   * Set the marker type to use when adding new markers
   * @param {number} type - EcgAnnotationTypes enum value
   */
  setSelectedMarkerType(type) {
    this.selectedMarkerType = type;
  }

  /**
   * Get currently selected marker type
   * @returns {number}
   */
  getSelectedMarkerType() {
    return this.selectedMarkerType;
  }

  /**
   * Initialize with markers from server
   * @param {Array} markers - Array of marker objects with x, isR, title, type properties
   */
  setOriginalMarkers(markers) {
    // Ensure all markers have the type property (for backwards compatibility)
    this.originalMarkers = (markers || []).map(m => ({
      ...m,
      // Backend now sends type as int; fallback to R_PEAK_TYPE if missing
      type: m.type !== undefined ? m.type : (m.isR ? R_PEAK_TYPE : NOT_QRS_TYPE)
    }));
  }

  /**
   * Get effective markers (original + additions - deletions)
   * @returns {Array} Combined marker array for display
   */
  getEffectiveMarkers() {
    // Start with original markers, excluding deleted ones.
    // Filter by BOTH position AND type - only hide markers that exactly match a deletion.
    // This ensures clicking on one marker doesn't hide nearby markers of different types.
    const effective = this.originalMarkers.filter(original =>
      !this.pendingDeletions.some(deleted =>
        Math.abs(deleted.x - original.x) <= this.PROXIMITY_MS && deleted.type === original.type
      )
    );
    // Normalize pending additions
    const normalizedAdditions = this.pendingAdditions.map(m => ({
      x: m.x,
      isR: isRPeakType(m.type),
      type: m.type,
      title: m.title || null
    }));
    return [...effective, ...normalizedAdditions];
  }

  /**
   * Find the closest marker near the given X position using progressive proximity search.
   * First tries smallest threshold, then expands if no marker found.
   * Within each threshold, returns the marker closest to the click position.
   * @param {number} x - X position in milliseconds
   * @returns {Object|null} Found marker or null
   */
  findMarkerNear(x) {
    for (const threshold of PROXIMITY_THRESHOLDS_MS) {
      // Find all markers within this threshold
      const markersInRange = this.originalMarkers.filter(m => Math.abs(m.x - x) <= threshold);

      if (markersInRange.length > 0) {
        // Return the closest marker to the click position
        return markersInRange.reduce((closest, current) =>
          Math.abs(current.x - x) < Math.abs(closest.x - x) ? current : closest
        );
      }
    }
    return null;
  }

  /**
   * Find ALL markers within max proximity of the given X position.
   * @param {number} x - X position in milliseconds
   * @returns {Array} Array of markers within proximity (may be empty)
   */
  findMarkersNear(x) {
    return this.originalMarkers.filter(m => Math.abs(m.x - x) <= this.PROXIMITY_MS);
  }

  /**
   * Find a pending addition near the given X position
   * @param {number} x - X position in milliseconds
   * @returns {Object|null} Found pending marker or null
   */
  findPendingAdditionNear(x) {
    return this.pendingAdditions.find(m => Math.abs(m.x - x) <= this.PROXIMITY_MS) || null;
  }

  /**
   * Check if a specific marker (by x and type) is already pending deletion.
   * If type is not provided, checks if ANY marker at x is pending deletion.
   * @param {number} x - X position in milliseconds
   * @param {number} [type] - Optional marker type to check
   * @returns {boolean}
   */
  isMarkedForDeletion(x, type) {
    if (type !== undefined) {
      return this.pendingDeletions.some(m =>
        Math.abs(m.x - x) <= this.PROXIMITY_MS && m.type === type
      );
    }
    return this.pendingDeletions.some(m => Math.abs(m.x - x) <= this.PROXIMITY_MS);
  }

  /**
   * Handle a click at the given X position.
   * Returns the action taken for UI updates.
   *
   * Deletion behavior: Only ONE marker is deleted per click (the closest one found
   * using progressive proximity search). This gives users precise control when
   * multiple markers are close together.
   *
   * @param {number} clickedX - X position of the click in milliseconds
   * @param {number} [markerType] - Optional marker type to add (uses selectedMarkerType if not provided)
   * @returns {{action: string, marker: Object}} Action taken and affected marker
   */
  handleClick(clickedX, markerType) {
    const pendingAdded = this.findPendingAdditionNear(clickedX);
    const existingMarker = this.findMarkerNear(clickedX);

    if (pendingAdded) {
      // Clicked on a marker added in this session - remove it from pending
      // Use exact match on position and type to only remove the clicked one
      this.pendingAdditions = this.pendingAdditions.filter(
        m => !(Math.abs(m.x - pendingAdded.x) <= this.PROXIMITY_MS && m.type === pendingAdded.type)
      );
      return { action: 'removed_pending', marker: pendingAdded };
    }

    if (existingMarker && !this.isMarkedForDeletion(existingMarker.x, existingMarker.type)) {
      // Clicked on an existing marker from server - mark for deletion (ONE marker only)
      const deletionMarker = {
        x: existingMarker.x,
        type: existingMarker.type,
        isR: existingMarker.isR
      };
      this.pendingDeletions.push(deletionMarker);
      return { action: 'marked_for_deletion', marker: deletionMarker };
    }

    if (!existingMarker || this.isMarkedForDeletion(existingMarker?.x, existingMarker?.type)) {
      // Clicked on empty space or on an already-deleted marker spot - add new marker
      const typeToAdd = markerType !== undefined ? markerType : this.selectedMarkerType;
      const newMarker = {
        x: clickedX,
        type: typeToAdd,
        isR: isRPeakType(typeToAdd),
        title: getMarkerDisplayLabel(typeToAdd)
      };
      this.pendingAdditions.push(newMarker);
      return { action: 'added', marker: newMarker };
    }

    return { action: 'none', marker: null };
  }

  /**
   * Add a marker at the given X position
   * @param {number} x - X position in milliseconds
   * @param {number} [type] - Marker type (EcgAnnotationTypes value, default R_PEAK_TYPE)
   * @returns {Object} The added marker
   */
  addMarker(x, type = R_PEAK_TYPE) {
    const newMarker = {
      x,
      type,
      isR: isRPeakType(type),
      title: getMarkerDisplayLabel(type)
    };
    this.pendingAdditions.push(newMarker);
    return newMarker;
  }

  /**
   * Remove a pending addition
   * @param {number} x - X position in milliseconds
   * @returns {boolean} True if a marker was removed
   */
  removePendingAddition(x) {
    const initialLength = this.pendingAdditions.length;
    this.pendingAdditions = this.pendingAdditions.filter(
      m => Math.abs(m.x - x) > this.PROXIMITY_MS
    );
    return this.pendingAdditions.length < initialLength;
  }

  /**
   * Mark an existing marker for deletion
   * @param {number} x - X position in milliseconds
   * @param {number} [type] - Marker type (EcgAnnotationTypes value, default R_PEAK_TYPE)
   */
  markForDeletion(x, type = R_PEAK_TYPE) {
    if (!this.isMarkedForDeletion(x)) {
      this.pendingDeletions.push({ x, type });
    }
  }

  /**
   * Check if there are any pending changes
   * @returns {boolean}
   */
  hasPendingChanges() {
    return this.pendingAdditions.length > 0 || this.pendingDeletions.length > 0;
  }

  /**
   * Get data for saving to server
   * @returns {{additions: Array, deletions: Array}}
   */
  getPendingChanges() {
    return {
      additions: [...this.pendingAdditions],
      deletions: [...this.pendingDeletions]
    };
  }

  /**
   * Clear all pending changes (e.g., after successful save or cancel)
   */
  clearPendingChanges() {
    this.pendingAdditions = [];
    this.pendingDeletions = [];
  }

  /**
   * Reset manager to initial state
   */
  reset() {
    this.originalMarkers = [];
    this.pendingAdditions = [];
    this.pendingDeletions = [];
    this.selectedMarkerType = R_PEAK_TYPE;
  }
}

export default MarkerEditingManager;
